import {
  BoundRadioGroupField,
  BoundSelectField,
  Button,
  FormLines,
  ModalBody,
  ModalFooter,
  ModalHeader,
  useComputed,
  useModal,
} from "@homebound/beam";
import { baseDownloadUrl } from "src/context";
import { BidPackageBidderKind } from "src/generated/graphqlTypes";
import { ObjectConfig, useFormState } from "src/utils/formState";
import { openInSelf } from "src/utils/window";
import { PlanBasedBidsTableV2_BidPackageFragment } from "../plan-based-bid-package/components/PlanBasedBidsTableV2.generated";

type DownloadBidSheetV2ModalProps = { bidPackage: PlanBasedBidsTableV2_BidPackageFragment };

export function DownloadBidSheetV2Modal({ bidPackage }: DownloadBidSheetV2ModalProps) {
  const { closeModal } = useModal();
  const bidders = bidPackage.activeBidders;
  const formState = useFormState({
    config: formConfig,
    init: { onlyOnce: true, input: { includeCosts: "yes" as const, bidderId: bidders.first?.id } },
  });
  const includeCosts = useComputed(() => formState.includeCosts.value === "yes", [formState]);

  function handleDownload() {
    const bidder = includeCosts ? bidders.find((b) => b.id === formState.bidderId.value) : undefined;
    if (bidPackage.isUnitBased) {
      // Unit-based costs pre-fill from the bidder's latest BCR. A bidder with no contract yet falls back to the blank template.
      downloadUnitBasedBidPackage(
        bidPackage.id,
        bidder?.bidContract ? { bidContractRevisionId: bidder.bidContract.latestRevision.id } : {},
      );
    } else {
      // Trade partner bidders route through their BidPackageRequest, internal estimates through its latest BCR.
      downloadPlanBasedBidPackageV2(
        bidPackage.id,
        bidder?.kind === BidPackageBidderKind.TradeRequest && bidder.request
          ? { bidPackageRequestId: bidder.request.id }
          : bidder?.kind === BidPackageBidderKind.InternalEstimate && bidder.bidContract
            ? { bidContractRevisionId: bidder.bidContract.latestRevision.id }
            : {},
      );
    }
    closeModal();
  }

  return (
    <div data-testid="downloadBidSheetV2Modal">
      <ModalHeader>Download Bid Sheet</ModalHeader>
      <ModalBody>
        <FormLines>
          <BoundRadioGroupField
            field={formState.includeCosts}
            label="Do you want to include costs in your download?"
            options={[
              { label: "No, download with blank costs column.", value: "no" },
              { label: "Yes, include trade costs.", value: "yes" },
            ]}
          />
          {includeCosts && <BoundSelectField field={formState.bidderId} label="Trade" options={bidders} />}
        </FormLines>
      </ModalBody>
      <ModalFooter>
        <Button label="Cancel" variant="tertiary" onClick={closeModal} />
        <Button label="Download" onClick={handleDownload} />
      </ModalFooter>
    </div>
  );
}

type FormValues = { includeCosts?: "yes" | "no" | null; bidderId?: string | null };

const formConfig: ObjectConfig<FormValues> = { includeCosts: { type: "value" }, bidderId: { type: "value" } };

function downloadPlanBasedBidPackageV2(
  bidPackageId: string,
  opts: { bidPackageRequestId?: string; bidContractRevisionId?: string } = {},
) {
  const { bidPackageRequestId, bidContractRevisionId } = opts;
  const params = new URLSearchParams({
    type: "planBasedBidPackageV2",
    bidPackageId,
    ...(bidPackageRequestId ? { bidPackageRequestId } : {}),
    ...(bidContractRevisionId ? { bidContractRevisionId } : {}),
  });
  openInSelf(`${baseDownloadUrl()}/xlsx?${params.toString()}`);
}

function downloadUnitBasedBidPackage(bidPackageId: string, opts: { bidContractRevisionId?: string } = {}) {
  const { bidContractRevisionId } = opts;
  const params = new URLSearchParams({
    type: "unitBasedBidPackage",
    bidPackageId,
    ...(bidContractRevisionId ? { bidContractRevisionId } : {}),
  });
  openInSelf(`${baseDownloadUrl()}/xlsx?${params.toString()}`);
}

/**
 * Returns a download handler for a V2 plan-based or unit-based bid package's bid sheet: when any
 * active bidder has entered costs, opens the include-costs modal, otherwise downloads the blank
 * costs sheet directly.
 */
export function useDownloadBidSheetV2() {
  const { openModal } = useModal();
  return (bidPackage: PlanBasedBidsTableV2_BidPackageFragment) =>
    bidPackage.activeBidders.some((b) => b.hasCosts)
      ? openModal({ content: <DownloadBidSheetV2Modal bidPackage={bidPackage} /> })
      : bidPackage.isUnitBased
        ? downloadUnitBasedBidPackage(bidPackage.id)
        : downloadPlanBasedBidPackageV2(bidPackage.id);
}
